import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Divider } from '@bringg/react-components';
import noop from 'lodash/noop';
import { useOutsideClick } from '@bringg-frontend/hooks';
import { PresetViewScope } from '@bringg/types';

import { ClientSideConfig } from '../stores/stores/types';
import { getPresetIcon } from '../utils/preset-views-service';
import { CreateNewPreset } from './create-new-preset';
import { useStores } from 'bringg-web/recipes';
import { ManagePresetViews } from '../manage-preset-views';

import styles from './preset-views-picker.module.scss';

interface GroupProps {
	name: string;
	options: Array<{
		id: number | string;
		icon: BringgFontIcons;
		name: string;
		onClick: () => void;
		testId?: string;
		active?: boolean;
		edited?: boolean;
		disabled?: boolean;
	}>;
	disabled?: boolean;
}

const Group = ({ name, options, disabled }: GroupProps) => {
	const { t } = useTranslation();

	return (
		<div className={styles.group}>
			<span className={styles.name}>{t(name)}</span>
			<div className={styles.groupList}>
				{options.map(item => (
					<div
						data-test-id={item.testId}
						className={classNames(styles.option, {
							[styles.disabled]: item.disabled || disabled,
							[styles.active]: item.active
						})}
						key={item.id}
						onClick={disabled || item.disabled || item.active ? noop : item.onClick}
					>
						<BringgIcon iconName={item.icon} className={styles.presetViewIcon} />
						<span className={styles.itemName} title={item.name}>
							{item.name}
						</span>
						{item.edited ? <span className={styles.optionEdited}>{t('PRESET_VIEWS.EDITED')}</span> : null}
						{item.active ? <BringgIcon iconName={BringgFontIcons.Selected} /> : null}
					</div>
				))}
			</div>
		</div>
	);
};

interface Props {
	activePreset: ClientSideConfig;
	presetViews: ClientSideConfig[];
	className?: string;
}

export const PresetViewsPicker = observer(({ presetViews, activePreset, className }: Props) => {
	const ref = useRef<HTMLDivElement>();
	const { t } = useTranslation();
	const {
		usersStore: { currentUser }
	} = useStores();

	const [opened, setOpened] = useState(false);
	const [applyingPreset, setApplyingPreset] = useState(false);
	const [showSaveAsModal, setShowSaveAsModal] = useState(false);
	const [showManagePresetsModal, setShowManagePresetsModal] = useState(false);

	const activatePreset = useCallback(
		async (activatePresetForView: ClientSideConfig['activatePresetForView']) => {
			if (applyingPreset) {
				return;
			}

			setApplyingPreset(true);
			await activatePresetForView();
			setApplyingPreset(false);
		},
		[applyingPreset]
	);

	const selectablePresets = useMemo<GroupProps['options']>(() => {
		return presetViews.map(({ id, name, scope, edited, activatePresetForView }) => ({
			id,
			name,
			scope,
			testId: `preset-item-${id}`,
			icon: getPresetIcon(scope),
			active: id === activePreset?.id,
			onClick: async () => activatePreset(activatePresetForView),
			edited: edited
		}));
	}, [activePreset, activePreset.edited, presetViews, activatePreset]);

	const handleLabelClicked = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			// Prevent occasional behaviour when open click also triggers outside click and closes the popup
			event.stopPropagation();
			setOpened(!opened);
		},
		[opened]
	);

	const openSaveAsModal = useCallback(() => {
		setShowSaveAsModal(true);
	}, []);

	const closeSaveAsModal = useCallback(() => {
		setShowSaveAsModal(false);
	}, []);

	const openShowManagePresetsModal = useCallback(() => {
		setShowManagePresetsModal(true);
	}, []);

	const closeShowManagePresetsModal = useCallback(() => {
		setShowManagePresetsModal(false);
	}, []);

	const handleSavePresetView = useCallback(async () => {
		await activePreset.save();
	}, [activePreset]);

	const actions = useMemo(() => {
		const actions: GroupProps['options'] = [
			{
				id: 'reset',
				testId: 'preset-views-reset',
				icon: BringgFontIcons.Rotate,
				name: t('PRESET_VIEWS.RESET'),
				onClick: activePreset.reset,
				disabled: !activePreset.edited
			},
			{
				id: 'save',
				testId: 'preset-views-save',
				icon: BringgFontIcons.Save,
				name: t('PRESET_VIEWS.SAVE'),
				onClick: handleSavePresetView,
				disabled:
					!activePreset.edited || (!currentUser.admin && activePreset.scope === PresetViewScope.Merchant)
			},
			{
				id: 'save_as',
				testId: 'preset-views-save_as',
				icon: BringgFontIcons.EditSquare,
				name: t('PRESET_VIEWS.SAVE_AS'),
				onClick: openSaveAsModal,
				disabled: !activePreset.edited
			},
			{
				id: 'manage',
				testId: 'preset-views-manage',
				icon: BringgFontIcons.Preferences,
				name: t('PRESET_VIEWS.MANAGE_DISPLAYS_ACTION'),
				onClick: openShowManagePresetsModal
			}
		];

		return actions;
	}, [activePreset, activePreset.edited, currentUser, handleSavePresetView]);

	useOutsideClick(
		ref,
		useCallback(() => {
			if (opened && !showManagePresetsModal && !showSaveAsModal) {
				setOpened(false);
			}
		}, [opened, showSaveAsModal, showManagePresetsModal])
	);

	return (
		<>
			<div className={classNames(styles.presetViewPicker, className)}>
				<div
					className={classNames(`${styles.pickerLabel}`, { [styles.opened]: opened })}
					onClick={handleLabelClicked}
				>
					<BringgIcon iconName={getPresetIcon(activePreset.scope)} className={styles.prefixIcon} />
					<div className={styles.pickerLabelText} title={activePreset?.name}>
						<span>{t('PRESET_VIEWS.DISPLAY')}</span>
						{`: ${activePreset?.name}`}
					</div>
					<BringgIcon iconName={BringgFontIcons.Chevron} className={styles.labelChevron} />
				</div>
				{opened ? (
					<div className={styles.pickerContent} ref={ref}>
						<Group
							name={t('PRESET_VIEWS.DISPLAYS')}
							options={selectablePresets}
							disabled={activePreset.loading || applyingPreset}
						/>
						<Divider className={styles.groupDivider} />
						<Group
							name={t('PRESET_VIEWS.ACTIONS')}
							options={actions}
							disabled={activePreset.loading || applyingPreset}
						/>
					</div>
				) : null}
			</div>
			<CreateNewPreset visible={showSaveAsModal} activePreset={activePreset} onClose={closeSaveAsModal} />
			<ManagePresetViews
				visible={showManagePresetsModal}
				presetViews={presetViews}
				activePreset={activePreset}
				onClose={closeShowManagePresetsModal}
			/>
		</>
	);
});
